/**
 * Keeps HTML chips pinned to a corner of their cytoscape nodes: the "+N" expansion hint (top
 * right) and the "changed" marker (top left, PRD-0075 R-C2). Positions are written straight to
 * the chips' styles on every cytoscape render, so panning and zooming never reconcile React;
 * a chip scales with the zoom the way a native label does, clamped at extreme zooms.
 */
import { type RefObject, useEffect } from 'react'
import type { CytoscapeGraphRef } from '@/components/neo4j/cytoscape/CytoscapeGraph'

export type NodeCorner = 'top-right' | 'top-left'

interface NodeCornerAnchors {
  graphRef: RefObject<CytoscapeGraphRef | null>
  wrapperRef: RefObject<HTMLElement | null>
  /** Node id → its chip; filled by the chips' `ref` callbacks. */
  chips: RefObject<Map<string, HTMLElement>>
  /** A stable snapshot of the anchored ids (`a,b,c`): the anchoring restarts when it changes. */
  idsKey: string
  corner: NodeCorner
}

type Cy = ReturnType<NonNullable<CytoscapeGraphRef['getCy']>>

interface RenderedNode {
  renderedPosition: () => { x: number; y: number }
  renderedOuterWidth: () => number
}

function writePositions(
  cy: NonNullable<Cy>,
  wrapper: HTMLElement | null,
  chips: Map<string, HTMLElement>,
  corner: NodeCorner
) {
  const container = cy.container() as HTMLElement | null
  const cr = container?.getBoundingClientRect()
  const wrapperRect = wrapper?.getBoundingClientRect()
  if (!cr || !wrapperRect) return
  const dx = cr.left - wrapperRect.left
  const dy = cr.top - wrapperRect.top
  const scale = Math.max(0.4, Math.min(cy.zoom(), 2.5))
  for (const [id, chip] of chips) {
    const el = cy.$id(id)
    const pos = el.length === 0 ? null : (el as unknown as RenderedNode).renderedPosition()
    if (!pos) {
      chip.style.display = 'none'
      continue
    }
    chip.style.display = ''
    // The node's true corner — `renderedOuterWidth` already includes zoom.
    const r = ((el as unknown as RenderedNode).renderedOuterWidth?.() ?? 24) / 2 - 3
    chip.style.left = `${dx + pos.x + (corner === 'top-right' ? r : -r)}px`
    chip.style.top = `${dy + pos.y - r}px`
    // `translate(-50%,-50%)` centres the chip on the corner; `scale()` tracks the node's size.
    chip.style.transform = `translate(-50%, -50%) scale(${scale})`
  }
}

export function useNodeCornerAnchors({
  graphRef,
  wrapperRef,
  chips,
  idsKey,
  corner,
}: NodeCornerAnchors) {
  useEffect(() => {
    if (!idsKey) return
    // Cytoscape mounts asynchronously: poll for the instance until it is ready, then follow
    // the viewport. Without the poll the effect can race past the mount and never attach.
    let pollHandle: ReturnType<typeof setInterval> | null = null
    let cy: Cy = null
    let raf = 0
    const write = () => {
      if (cy) writePositions(cy, wrapperRef.current, chips.current, corner)
    }
    const schedule = () => {
      if (raf !== 0) return
      raf = requestAnimationFrame(() => {
        raf = 0
        write()
      })
    }
    const attach = () => {
      const instance = graphRef.current?.getCy?.()
      if (!instance) return false
      cy = instance
      write()
      instance.on('render viewport pan zoom', schedule)
      return true
    }
    if (!attach()) {
      pollHandle = setInterval(() => {
        if (attach() && pollHandle) {
          clearInterval(pollHandle)
          pollHandle = null
        }
      }, 100)
    }
    return () => {
      if (pollHandle) clearInterval(pollHandle)
      if (cy) cy.off('render viewport pan zoom', schedule)
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [idsKey, corner, graphRef, wrapperRef, chips])
}
